import React, { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import Body from 'ui/Typography/Body';
import Button from '@ingka/button';
import Title from 'ui/Typography/Title';
import '@ingka/svg-icon/dist/style.css';
import '@ingka/button/dist/style.css';
import '@ingka/modal/dist/style.css';
import '@ingka/svg-icon/dist/style.css';
import '@ingka/focus/dist/style.css';
import Modal, { ModalFooter, ModalHeader, ModalBody, Sheets } from '@ingka/modal';
import { ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums/lib';
import { useIntl } from 'react-intl';
import {
  hideDrillTemplateSheet,
  isSheetVisible,
  setDrillTemplateSheetDismissed,
  shouldAddDrillTemplate,
  wasDismissed
} from './HandlesDrillTemplate';
import DrillTemplateImage from './drilltemplateimg.png';
import { useAppDispatch } from 'ducks/hooks';
import { getMarketProducts } from 'ducks/products';
import { ProductType } from '@inter-ikea-storageone/range';
import { useCore } from 'contexts/core';
import styled from 'styled-components';
import { invalidateVpcCode } from 'ducks/vpc';
import { resetDesignId } from 'ducks/designId';
import media from 'ui/media';
import useFetchShoppingProducts from 'hooks/useFetchShoppingProducts';
import { calculateTotalPrice } from 'components/Header/TotalPrice';
import Price from 'ui/Price/Price';
import { isMobile } from 'utils/platform';

const Image = styled.img`
  width: 329px;
`;

const ImageWrapper = styled.div`
  display: flex;
  justify-content: center;
`;

const TitleWrapper = styled.div`
  ${media.mobile`
    width: 100%;
    padding: 0.5rem 0;
  `}
`;

const SpanDivider = styled.span`
  padding: 0 2px;
`;

const HandlesDrillTemplateSheet = () => {
  const { formatMessage } = useIntl();
  const dispatch = useAppDispatch();
  const products = useSelector(getMarketProducts);
  const sheetVisible = useSelector(isSheetVisible);
  const wasSheetDismissed = useSelector(wasDismissed);
  const addDrillTemplate = useSelector(shouldAddDrillTemplate);
  const coreApi = useCore();

  const drillTemplateProduct = useMemo(() => {
    return products.find(product => product.type === ProductType.HANDLE_DRILL_TEMPLATE);
  }, [products]);

  const onFinalize = () => {
    if (addDrillTemplate !== false) {
      dispatch(invalidateVpcCode());
      dispatch(resetDesignId());
      const drillTemplateEntity =
        drillTemplateProduct && coreApi.entities.create(drillTemplateProduct.coreProductId);
      drillTemplateEntity &&
        coreApi.entities.placementService.forcePlaceEntity(drillTemplateEntity);
    }
    onDismiss();
  };

  const onDismiss = () => {
    dispatch(hideDrillTemplateSheet());
    dispatch(setDrillTemplateSheetDismissed(true));
  };

  useEffect(() => {
    const onHistoryEvent = () => {
      const handlesInScene = coreApi.entities.getByProductTypes([
        ProductType.HANDLE,
        ProductType.KNOB
      ]);

      const drillTemplateInScene = coreApi.entities.getByProductType(
        ProductType.HANDLE_DRILL_TEMPLATE
      )[0];
      if (handlesInScene.length === 0 && drillTemplateInScene) {
        coreApi.entities.delete(drillTemplateInScene);
        dispatch(setDrillTemplateSheetDismissed(false));
      }
    };

    return coreApi.subscribe('history', onHistoryEvent);
  }, [coreApi, dispatch]);

  const { shoppingItemsWithPrice } = useFetchShoppingProducts(
    drillTemplateProduct ? [drillTemplateProduct] : []
  );
  const totalPrice = calculateTotalPrice(shoppingItemsWithPrice);

  return drillTemplateProduct ? (
    <Modal
      data-testid="handles-drill-template-sheet"
      visible={!!sheetVisible && !wasSheetDismissed}
      handleCloseBtn={onDismiss}
    >
      <Sheets
        title={formatMessage({ id: 'storageone.handlesDrillTemplateSheetTitle' })}
        header={
          <ModalHeader
            data-testid="modal-header"
            ariaCloseTxt={formatMessage({ id: 'storageone.closeButton' })}
          />
        }
        footer={
          <ModalFooter
            data-testid="handles-drill-template-sheet-footer-buttons"
            stacked
            renderBorder
            reverse={isMobile()}
          >
            <Button
              text={formatMessage({ id: 'storageone.drillTemplateAddADrillTemplate' })}
              onClick={() => onFinalize()}
              type="primary"
              data-testid="drill-template-sheet-finalize-btn"
            >
              <SpanDivider />
              <Price amount={totalPrice} />
            </Button>
            <Button
              text={formatMessage({ id: 'storageone.drillTemplateContinueWithoutDrillTemplate' })}
              onClick={() => onDismiss()}
              type="secondary"
              data-testid="drill-template-sheet-dismiss-btn"
              fluid
            ></Button>
          </ModalFooter>
        }
      >
        <ModalBody style={{ paddingTop: '0px' }}>
          <TitleWrapper>
            <Title type={ThemeFontStyleTypeEnum.headingMedium} mb="md">
              {formatMessage({ id: 'storageone.handlesDrillTemplateSheetTitle' })}
            </Title>
          </TitleWrapper>
          <Body type={ThemeFontStyleTypeEnum.bodyMedium} mb="xxxl">
            {formatMessage({ id: 'storageone.handlesDrillTemplateSheetDescription' })}
          </Body>
          <ImageWrapper>
            <Image
              src={DrillTemplateImage}
              alt={formatMessage({ id: 'storageone.handlesDrillTemplateSheetTitle' })}
            />
          </ImageWrapper>
        </ModalBody>
      </Sheets>
    </Modal>
  ) : (
    <></>
  );
};

export default HandlesDrillTemplateSheet;
